import { useMemo, useRef } from 'react';
import mergeRefs from 'react-merge-refs';
import { AnimatePresence, motion } from 'framer-motion';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';
import type { WatermarkBlock } from '@frameio/graphql-client/generated';
import { Box, useMeasure } from '@frameio/vapor';
import {
  BASE_FOR_SCALE,
  HORIZONTAL_BORDER_PX,
  VERTICAL_BORDER_PX,
} from '../../constants';
import { getTransformOrigin } from '../../utils/getTransformOrigin';
import { useReferencePointLayout } from '../../utils/useReferencePointLayout';
import PreviewImage from './PreviewImage';
import WatermarkTemplateBlock from './WatermarkTemplateBlock';

const BORDER_WIDTH = 3;
const WMID_PREVIEW_PLACEHOLDER_URL = '/images/background-16-9.jpg';

const variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const TemplateBlock = ({
  block,
  size,
}: {
  block: WatermarkBlock;
  parentRef: React.RefObject<HTMLDivElement | null>;
  size: number[];
}) => {
  const ref = useRef(null);
  const scale = size[0] / BASE_FOR_SCALE;

  // The following style computations are similar to what's found in WatermarkTemplateBlockPositionContainer,
  // but different enough to not be valuable to reuse verbatim.
  // However, since the same hook for calculation position is used, the layout should match what is seen in the SessionWatermarkTemplateEditor.
  const { position, relativeOffset } = useReferencePointLayout({
    referencePoint: block.positionReferencePoint || block.position,
    positionMax: size as [number, number],
    positionNormalized: [block.positionX, block.positionY],
    positionOffset: [0, 0],
  });

  const isScrolling =
    block.scrollText === 'LEFT_TO_RIGHT' ||
    block.scrollText === 'RIGHT_TO_LEFT';

  const style: CSSProperties = useMemo(() => {
    const [x, y] = position;
    const [midpointOffsetX, midpointOffsetY] = relativeOffset;
    const originTranslation = isScrolling
      ? `translate(0, ${midpointOffsetY}%)`
      : `translate(${midpointOffsetX}%, ${midpointOffsetY}%)`;
    const scaleTransformation = isScrolling
      ? `scale(1, ${scale})`
      : `scale(${scale})`;

    return {
      left: isScrolling ? 1 : x || 0,
      top: y || 0,
      textAlign: isScrolling
        ? 'center'
        : (block?.textAlignment as CSSProperties['textAlign']),
      transform: `${originTranslation} rotate(${block.rotation}deg) ${scaleTransformation}`,
      transformOrigin: getTransformOrigin(
        block.positionReferencePoint || block.position
      ),
      padding: `${VERTICAL_BORDER_PX - BORDER_WIDTH}px
        ${isScrolling ? 0 : HORIZONTAL_BORDER_PX - BORDER_WIDTH}px`,
      borderLeftWidth: isScrolling ? 0 : undefined,
      borderRightWidth: isScrolling ? 0 : undefined,
      // Adjust width to account for border width
      width: isScrolling ? 'calc(100% - 2px)' : undefined,
    };
  }, [block, isScrolling, position, relativeOffset, scale]);

  return (
    <TemplateBlockContainer ref={ref} style={style}>
      <WatermarkTemplateBlock
        positionMaxX={size[0]}
        scaleX={scale}
        value={block}
      />
    </TemplateBlockContainer>
  );
};

const WatermarkTemplatePreview = ({ watermarkTemplateEntity }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [measureRef, measureRect] = useMeasure();

  return (
    <Box
      alignItems="center"
      display="flex"
      height="100%"
      overflow="hidden"
      position="relative"
    >
      <PreviewContainer ref={mergeRefs([ref, measureRef])}>
        {watermarkTemplateEntity && (
          <TemplateBlocks>
            <AnimatePresence>
              <motion.div
                key={watermarkTemplateEntity.id}
                animate="visible"
                exit="hidden"
                initial="hidden"
                transition={{
                  ease: 'easeInOut',
                  duration: 0.15,
                }}
                variants={variants}
              >
                {Object.values(watermarkTemplateEntity.blocks).map(
                  (block, index) => {
                    return (
                      <TemplateBlock
                        key={index}
                        block={block}
                        parentRef={ref}
                        size={[measureRect.width, measureRect.height]}
                      />
                    );
                  }
                )}
              </motion.div>
            </AnimatePresence>
          </TemplateBlocks>
        )}
        <PreviewImage src={WMID_PREVIEW_PLACEHOLDER_URL} />
      </PreviewContainer>
    </Box>
  );
};

export default WatermarkTemplatePreview;

const PreviewContainer = styled.div`
  height: 0px;
  overflow: hidden;
  padding-bottom: 56.25%;
  position: relative;
  width: 100%;
`;

const TemplateBlocks = styled.div`
  height: 100%;
  left: 0px;
  position: absolute;
  top: 0px;
  width: 100%;
  z-index: 2;
`;

const TemplateBlockContainer = styled.div`
  border-color: transparent;
  border-style: solid;
  border-width: ${BORDER_WIDTH}px;
  left: 0;
  position: absolute;
  top: 0;
  transform-origin: 0 0;
`;
